import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
} from '@react-google-maps/api';

const LIBRARIES: ('places')[] = ['places'];

interface LocationMapProps {
  onLocationSelect: (lat: number, lng: number) => void;
  initialLat?: number;
  initialLng?: number;
  coverageStatus?: 'Available' | 'Unavailable' | 'Pending';
  center?: { lat: number; lng: number } | null;
}

const KSA_CENTER = { lat: 24.7, lng: 46.7 };

const pinColors: Record<string, string> = {
  Available: '#00A84F',
  Unavailable: '#dc3545',
  Pending: '#ffc107',
};

export const LocationMap: React.FC<LocationMapProps> = ({
  onLocationSelect,
  initialLat,
  initialLng,
  coverageStatus,
  center,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_MAPS_KEY ?? '',
    libraries: LIBRARIES,
  });

  const [marker, setMarker] = useState<{ lat: number; lng: number } | null>(
    initialLat != null && initialLng != null ? { lat: initialLat, lng: initialLng } : null,
  );
  const [mapCenter, setMapCenter] = useState(KSA_CENTER);
  const mapRef = useRef<google.maps.Map | null>(null);

  // Auto-center on user's location when map first loads (no pin placed)
  useEffect(() => {
    if (!isLoaded) return;
    navigator.geolocation?.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setMapCenter(loc);
        if (mapRef.current) {
          mapRef.current.panTo(loc);
          mapRef.current.setZoom(14);
        }
      },
      () => { /* permission denied — stay on KSA_CENTER */ },
    );
  }, [isLoaded]);

  useEffect(() => {
    if (!center || !mapRef.current) return;
    setMarker(center);
    mapRef.current.panTo(center);
    mapRef.current.setZoom(14);
  }, [center]);

  const handleMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat != null && lng != null) {
        setMarker({ lat, lng });
        onLocationSelect(lat, lng);
      }
    },
    [onLocationSelect],
  );

  const handleMarkerDrag = useCallback(
    (e: google.maps.MapMouseEvent) => {
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat != null && lng != null) {
        setMarker({ lat, lng });
        onLocationSelect(lat, lng);
      }
    },
    [onLocationSelect],
  );

  const handleMyLocation = () => {
    navigator.geolocation.getCurrentPosition((pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      setMarker({ lat, lng });
      onLocationSelect(lat, lng);
      mapRef.current?.panTo({ lat, lng });
    });
  };

  if (loadError) return <div style={{ color: '#dc3545' }}>Failed to load map</div>;
  if (!isLoaded) return <div style={{ color: '#6c757d' }}>Loading map…</div>;

  const markerIcon = marker && coverageStatus
    ? {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: pinColors[coverageStatus] ?? '#ffc107',
        fillOpacity: 1,
        strokeWeight: 2,
        strokeColor: '#fff',
      }
    : undefined;

  return (
    <div style={{ position: 'relative' }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '400px', borderRadius: '8px' }}
        center={marker ?? mapCenter}
        zoom={marker ? 14 : 6}
        onClick={handleMapClick}
        onLoad={(map) => { mapRef.current = map; }}
        options={{ mapTypeControl: false, streetViewControl: false }}
      >
        {marker && (
          <MarkerF
            position={marker}
            draggable
            onDragEnd={handleMarkerDrag}
            icon={markerIcon}
          />
        )}
      </GoogleMap>
      <button
        onClick={handleMyLocation}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          padding: '8px 14px',
          background: '#fff',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: 600,
          boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
        }}
      >
        📍 My Location
      </button>
    </div>
  );
};
